import { ChevronLeft, ChevronRight, Flame, PackageOpen } from 'lucide-react';
import { formatPrice } from '../../lib/format';

/**
 * "Hot Product" - the counterparty's live store listings, from
 * GET /api/listings?sellerId=... . Shown under the tab content so it stays
 * visible in both the User Profile and My Orders states.
 *
 * The arrow collapses the whole details panel (per spec) rather than just this
 * section; the rail it collapses to carries the matching expand control.
 */
export default function HotProductPanel({ products, status, collapsed = false, onToggleCollapsed }) {
  return (
    <section
      aria-label="Hot products"
      className="overflow-hidden rounded-2xl border border-line bg-surface shadow-soft"
    >
      <header className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink">
          <Flame size={14} className="text-accent" />
          Hot Product
        </span>

        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand details panel' : 'Collapse details panel'}
          title={collapsed ? 'Expand details panel' : 'Collapse details panel'}
          className="grid h-7 w-7 place-items-center rounded-lg text-ink-3 transition hover:bg-surface-3 hover:text-ink"
        >
          {collapsed ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
        </button>
      </header>

      {status === 'loading' && (
        <div className="space-y-2 p-3">
          <span className="sr-only" role="status">
            Loading products…
          </span>
          {[0, 1, 2].map((row) => (
            <span key={row} className="flex items-center gap-3">
              <span className="h-12 w-12 shrink-0 animate-pulse rounded-xl bg-surface-3" />
              <span className="flex-1 space-y-2">
                <span className="block h-2.5 w-4/5 animate-pulse rounded bg-surface-3" />
                <span className="block h-2.5 w-1/3 animate-pulse rounded bg-surface-3" />
              </span>
            </span>
          ))}
        </div>
      )}

      {status !== 'loading' && products.length === 0 && (
        <div className="px-4 py-6 text-center">
          <span className="mx-auto grid h-10 w-10 place-items-center rounded-xl bg-surface-3 text-ink-3">
            <PackageOpen size={18} />
          </span>
          <p className="mt-2.5 text-[12.5px] font-medium text-ink">No live listings</p>
          <p className="mt-1 text-[11.5px] text-ink-3">
            This seller has nothing listed right now.
          </p>
        </div>
      )}

      {products.length > 0 && (
        <ul className="space-y-0.5 p-2">
          {products.map((product) => (
            <li key={product.id}>
              <a
                href={product.href}
                title={product.blurb}
                className="group flex items-center gap-3 rounded-xl p-2 transition hover:bg-surface-3"
              >
                {product.thumb ? (
                  <img
                    src={product.thumb}
                    alt=""
                    width="48"
                    height="48"
                    loading="lazy"
                    className="h-12 w-12 shrink-0 rounded-xl border border-line object-cover transition group-hover:scale-[1.04]"
                  />
                ) : (
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-line bg-surface-3 text-ink-3">
                    <PackageOpen size={16} />
                  </span>
                )}

                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 block text-[11.5px] leading-snug text-ink-2">
                    {product.title}
                  </span>
                  <span className="mt-1 block text-[12.5px] font-semibold text-accent tabular-nums">
                    {formatPrice(product.price)}
                  </span>
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
